import React, { useState } from 'react';
import { Send, Users, Shield, Award } from 'lucide-react';
import { Input } from '../components/ContactForm/Input';
import { ContactInfo } from '../components/ContactForm/ContactInfo';

interface FormData {
  name: string;
  email: string;
  profession: string;
  motivation: string;
}

interface FormErrors {
  [key: string]: string;
}

const membershipBenefits = [
  {
    icon: <Users className="h-8 w-8 text-blue-600" />,
    title: "Réseau d'Excellence",
    description: "Rejoignez un cercle sélect de décideurs et d'experts influents"
  },
  {
    icon: <Shield className="h-8 w-8 text-blue-600" />,
    title: "Événements Exclusifs",
    description: "Participez à des rencontres privilégiées avec des personnalités exceptionnelles"
  },
  {
    icon: <Award className="h-8 w-8 text-blue-600" />,
    title: "Intelligence Collective",
    description: "Contribuez à l'émergence d'idées novatrices pour façonner l'avenir de notre région"
  }
];

export const Membership = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    profession: '',
    motivation: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = () => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    
    if (!formData.profession.trim()) {
      newErrors.profession = 'La profession est requise';
    }
    
    if (!formData.motivation.trim()) {
      newErrors.motivation = 'La motivation est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);

    try {
      const form = e.target as HTMLFormElement;
      await fetch(form.action, {
        method: 'POST',
        body: new FormData(form)
      });

      setIsSuccess(true);
      setFormData({ name: '', email: '', profession: '', motivation: '' });
      
      // Reset form
      form.reset();
    } catch (error) {
      console.error('Error:', error);
      setErrors({ submit: 'Une erreur est survenue lors de l\'envoi du formulaire' });
    } finally {
      setIsSubmitting(false);
    }
    
    setTimeout(() => setIsSuccess(false), 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Devenir membre</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Rejoignez notre communauté et participez à l'aventure du Cercle Poincaré.
            Ensemble, faisons progresser la connaissance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {membershipBenefits.map((benefit, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="flex justify-center mb-4">
                {benefit.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
              <p className="text-gray-600">{benefit.description}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form 
              onSubmit={handleSubmit}
              action="https://formsubmit.co/cerclepoincare.001@gmail.com"
              method="POST"
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <input type="hidden" name="_subject" value="Nouvelle demande d'adhésion - Cercle Poincaré" />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_next" value="https://cercle-poincare.com/devenir-membre" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Input
                  label="Nom"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={errors.name}
                  placeholder="Votre nom"
                />
                <Input
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                  placeholder="votre@email.com"
                />
              </div>
              
              <div className="mb-6">
                <Input
                  label="Profession"
                  type="text"
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                  error={errors.profession}
                  placeholder="Votre profession ou domaine d'expertise"
                />
              </div>
              
              <div className="mb-6">
                <Input
                  label="Motivation"
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleChange}
                  error={errors.motivation}
                  placeholder="Pourquoi souhaitez-vous rejoindre le Cercle Poincaré ?"
                  textarea
                />
              </div>

              {errors.submit && (
                <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg">
                  {errors.submit}
                </div>
              )}

              <div className="flex items-center justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`
                    flex items-center px-6 py-3 rounded-lg text-white
                    transition-all duration-200
                    ${isSubmitting ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}
                  `}
                >
                  {isSubmitting ? (
                    <span>Envoi en cours...</span>
                  ) : (
                    <>
                      <span>Envoyer ma candidature</span>
                      <Send className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </div>

              {isSuccess && (
                <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-lg">
                  Votre candidature a été envoyée avec succès ! Nous vous contacterons prochainement.
                </div>
              )}
            </form>
          </div>

          <div>
            <ContactInfo
              email="cerclepoincare.001@gmail.com"
              phone="06 14 15 45 90"
              address="Nancy, France"
            />
          </div>
        </div>
      </div>
    </div>
  );
};